import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useToggle } from 'react-use'
import clsx from 'clsx'
import Debug from 'debug'
import { observer } from 'mobx-react-lite'
import { AnimatePresence, LayoutGroup, motion } from 'motion/react'
import { Trans } from '@lingui/react/macro'
import * as Dialog from '@radix-ui/react-dialog'
import { type QueryKey, useQueries } from '@tanstack/react-query'

import { trackEvent } from '@jotta/grpc-connect-client/clientEvent'
import { useFeatures } from '@jotta/grpc-connect-client/config'
import { useUsername } from '@jotta/grpc-connect-client/customer'
import { useDocumentEventListener } from '@jotta/hooks'
import {
  hasAdjustments,
  hasCrop,
  hasFilter,
  hasOverlay,
  hasRotate,
} from '@jotta/photo-editor/models'
import type { Photos } from '@jotta/types/Photos'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { Button } from '@jotta/ui/Button'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import { RadixModal } from '@jotta/ui/RadixModal'
import SvgCaretLeft from '@jotta/ui/SvgCaretLeft'
import SvgCaretRight from '@jotta/ui/SvgCaretRight'
import SvgChatCircle from '@jotta/ui/SvgChatCircle'
import SvgInfo from '@jotta/ui/SvgInfo'
import SvgPhotoSearch from '@jotta/ui/SvgPhotoSearch'
import SvgSliders from '@jotta/ui/SvgSliders'
import SvgX from '@jotta/ui/SvgX'
import { useBrandStore } from '@jotta/ui/useBrandTheme'
import { Breakpoint, useBreakpoint } from '@jotta/ui/useBreakpoint'

import type { CollectionRef } from '../../api/useCollection'
import { useAlbumId } from '../../api/useCollection'
import { type CarouselPhoto, usePhoto } from '../../api/usePhoto'
import { PhotoComments } from '../Comments/PhotoComments'
import { useCommentsOpenState } from '../Comments/useCommentsOpenState'
import { trackPersonPhotosAction } from '../PhotoActions/PhotoAction.tracking'
import type { ActionDispatcherProps } from '../PhotoActions/PhotoActions'
import { PhotoAction } from '../PhotoActions/PhotoActions'
import { PhotoInfo } from '../PhotoInfo/PhotoInfo'

import { MediaView } from './MediaView'
import type { PhotoEditorOverlayHandle } from './PhotoEditorOverlay'

import styles from './MediaCarousel.module.scss'

const debug = Debug('jotta:photos:MediaCarousel')

const loadPhotoEditorOverlay = () => import('./PhotoEditorOverlay')
const LazyPhotoEditorOverlay = lazy(() =>
  loadPhotoEditorOverlay().then(module => ({
    default: module.PhotoEditorOverlay,
  })),
)

export interface MediaCarouselProps {
  open: boolean
  collectionRef: CollectionRef
  commentsGroupId?: string
  ids: readonly string[]
  items: readonly Photos.Media[]
  currentId?: string
  keyField?: MediaCarouselKeyField
  itemQueryOptions?: (key: string) => MediaCarouselItemQueryOptions | undefined
  onNavigate?: (item?: { id: string; timestamp: bigint }) => void
  onCurrentMediaChange?: (id: string) => void
  onClose: (closed: boolean) => void
  onSlideChange?: (slider: string) => void
  slideShowMode?: boolean
  showOwnerName?: boolean
  showSimilarSearch?: boolean
  navOnQueryParam?: string
  actionScope?: 'default' | 'people'
}

export type MediaCarouselKeyField = 'id' | 'md5'

export interface MediaCarouselItemQueryOptions {
  queryKey: QueryKey
  queryFn: (context: { signal?: AbortSignal }) => Promise<unknown>
  select?: (data: unknown) => unknown
  staleTime?: number
  enabled?: boolean
}

function getMediaKey(
  item: Pick<CarouselPhoto, 'id' | 'md5'> | Photos.Media,
  key: MediaCarouselKeyField,
) {
  return item[key]
}

export function getMediaCarouselWindowKeys(
  ids: readonly string[],
  currentKey: string | undefined,
) {
  const index = currentKey ? ids.indexOf(currentKey) : -1

  if (index < 0) {
    return []
  }

  return ids.slice(Math.max(0, index - 1), index + 2)
}

export function getMediaCarouselRemovalTargetKey({
  nextKey,
  prevKey,
}: {
  nextKey?: string
  prevKey?: string
}) {
  return nextKey ?? prevKey
}

export function canEditMediaCarouselPhoto({
  actionScope,
  albumId,
  item,
  photoEditorEnabled,
  username,
}: {
  actionScope: 'default' | 'people'
  albumId: string | undefined
  item:
    | Pick<CarouselPhoto, 'isLivePhoto' | 'owner' | 'videoDurationSeconds'>
    | undefined
  photoEditorEnabled: boolean
  username: string | undefined
}) {
  return Boolean(
    actionScope !== 'people' &&
    albumId === undefined &&
    photoEditorEnabled &&
    item &&
    item.owner === username &&
    !(item.videoDurationSeconds && !item.isLivePhoto),
  )
}

function ActionButton({
  onClick,
  loading,
  label,
  icon,
}: ActionDispatcherProps) {
  const md = useBreakpoint(Breakpoint.md)
  return (
    <Button
      className="btn btn-ghost text-current"
      loading={loading}
      onClick={onClick}
      icon={icon}
    >
      {md && label}
    </Button>
  )
}

export const MediaCarousel = observer<MediaCarouselProps>(
  function MediaCarousel({
    collectionRef,
    ids: idsParam,
    items,
    onNavigate,
    onCurrentMediaChange,
    onClose,
    currentId,
    slideShowMode,
    commentsGroupId,
    showOwnerName = false,
    showSimilarSearch = false,
    navOnQueryParam,
    actionScope = 'default',
    keyField = 'id',
    itemQueryOptions,
    ...props
  }) {
    const username = useUsername()
    const { photoEditorEnabled } = useFeatures()
    const md = useBreakpoint(Breakpoint.md)
    const [showInfoWindow, toggleInfoWindow] = useToggle(false)
    const [showPhotoComments, togglePhotoComments] = useCommentsOpenState()
    const [editorOpen, setEditorOpen] = useState(false)
    // Imperative handle on the editor overlay so carousel-level close
    // paths (Esc, outside-click via Radix) can route through the
    // editor's discard-confirm flow rather than dropping unsaved edits.
    const editorRef = useRef<PhotoEditorOverlayHandle>(null)
    const [playing, setPlaying] = useState(false)
    const [params, setParams] = useSearchParams()

    const ids = useMemo(() => Array.from(new Set(idsParam)), [idsParam])
    const itemMap = useMemo(
      () =>
        Object.fromEntries(
          items.map(item => [getMediaKey(item, keyField), item]),
        ),
      [keyField, items],
    )

    const currentKey = currentId
    const index = currentKey ? ids.indexOf(currentKey) : -1
    const prevIndex = index > 0 ? index - 1 : -1
    const nextIndex = index >= 0 && index < ids.length - 1 ? index + 1 : -1
    const prevKey = ids[prevIndex] || undefined
    const nextKey = ids[nextIndex] || undefined
    const itemQueryKeys = useMemo(
      () => getMediaCarouselWindowKeys(ids, currentKey),
      [currentKey, ids],
    )
    const itemQueries = useQueries({
      queries: itemQueryOptions
        ? itemQueryKeys.map(key => {
            const options = itemQueryOptions(key)
            return options
              ? {
                  ...options,
                  enabled: options.enabled ?? true,
                }
              : {
                  queryKey: ['MediaCarouselItem', key],
                  queryFn: async () => undefined,
                  enabled: false,
                }
          })
        : [],
    })
    const queriedItemMap = useMemo(
      () =>
        Object.fromEntries(
          itemQueries
            .map((query, index) => {
              const key = itemQueryKeys[index]
              return key && query.data
                ? [key, query.data as CarouselPhoto]
                : undefined
            })
            .filter(
              (entry): entry is [string, CarouselPhoto] => entry !== undefined,
            ),
        ),
      [itemQueries, itemQueryKeys],
    )

    const albumId = useAlbumId(collectionRef)

    const fetchedCurrentItem = usePhoto(collectionRef, itemMap, currentKey, {
      enabled: Boolean(currentKey && !itemQueryOptions),
      // just the (legacy) photo id is probably not enough to reliably fetch the photo in public or followed albums.
      // be defensive for now and hope that photo refs in the v2 api solves this issue!
      throwOnError: false,
    })
    const fetchedPrevItem = usePhoto(collectionRef, itemMap, prevKey, {
      enabled: Boolean(prevKey && !itemQueryOptions),
      throwOnError: false, // see above
    })
    const fetchedNextItem = usePhoto(collectionRef, itemMap, nextKey, {
      enabled: Boolean(nextKey && !itemQueryOptions),
      throwOnError: false, // see above
    })
    const currentItem = itemQueryOptions
      ? currentKey
        ? queriedItemMap[currentKey]
        : undefined
      : fetchedCurrentItem
    const prevItem = itemQueryOptions
      ? prevKey
        ? queriedItemMap[prevKey]
        : undefined
      : fetchedPrevItem
    const nextItem = itemQueryOptions
      ? nextKey
        ? queriedItemMap[nextKey]
        : undefined
      : fetchedNextItem
    const currentNavigationKey = currentItem
      ? getMediaKey(currentItem, keyField)
      : currentKey
    const currentMediaId = currentItem?.id ?? currentId
    const currentTimestampNanos = currentItem?.timestampNanos
    const currentItemQuery =
      itemQueryOptions && currentKey
        ? itemQueries[itemQueryKeys.indexOf(currentKey)]
        : undefined

    if (currentItemQuery?.error) {
      throw currentItemQuery.error
    }

    useEffect(() => {
      if (currentMediaId && onNavigate && currentTimestampNanos) {
        onNavigate({
          id: currentMediaId,
          timestamp: currentTimestampNanos,
        })
      }
    }, [onNavigate, currentMediaId, currentTimestampNanos])

    useEffect(() => {
      if (currentNavigationKey && index >= 0) {
        onCurrentMediaChange?.(currentNavigationKey)
      }
    }, [currentNavigationKey, index, onCurrentMediaChange])

    const commentItemId = currentItem?.commentItemId

    // The editor only supports still photos and the
    // still half of a live photo on timeline (not albums, person photos).
    // Also gated by the `web.photo_editor` feature toggle.
    const canEditCurrentPhoto = canEditMediaCarouselPhoto({
      actionScope,
      albumId,
      item: currentItem,
      photoEditorEnabled,
      username,
    })

    const prevButton = useRef<HTMLButtonElement>(null)
    const nextButton = useRef<HTMLButtonElement>(null)
    const closeButton = useRef<HTMLButtonElement>(null)
    const prevSlide = useRef<HTMLDivElement>(null)
    const nextSlide = useRef<HTMLDivElement>(null)
    const currentSlide = useRef<HTMLDivElement>(null)
    const branding = useBrandStore()

    useEffect(() => {
      if (showInfoWindow) {
        togglePhotoComments(false)
      }
    }, [showInfoWindow, togglePhotoComments])
    useEffect(() => {
      if (showPhotoComments) {
        toggleInfoWindow(false)
      }
    }, [showPhotoComments, toggleInfoWindow])
    useEffect(() => {
      // Editor takes the whole viewport — close any side panels when opened.
      if (editorOpen) {
        toggleInfoWindow(false)
        togglePhotoComments(false)
      }
    }, [editorOpen, toggleInfoWindow, togglePhotoComments])

    const swipeOrigin = useRef(0)
    const offsetOrigin = useRef(0)

    useEffect(() => {
      const els = [
        prevSlide.current,
        currentSlide.current,
        nextSlide.current,
      ].filter((el): el is HTMLDivElement => Boolean(el))

      for (const el of els) {
        el.style.transition = ''
        el.style.transform = ''
      }

      swipeOrigin.current = 0
      offsetOrigin.current = 0
    }, [currentKey])

    const navigateTimeout = useRef(0)

    const handleTouchStart: React.TouchEventHandler<HTMLDivElement> =
      useCallback(e => {
        swipeOrigin.current = e.touches[0].clientX

        if (navigateTimeout.current) {
          window.clearTimeout(navigateTimeout.current)
          navigateTimeout.current = 0
        }

        const els = [
          prevSlide.current,
          currentSlide.current,
          nextSlide.current,
        ].filter((el): el is HTMLDivElement => Boolean(el))

        offsetOrigin.current = 0
        if (currentSlide.current) {
          offsetOrigin.current =
            currentSlide.current.getBoundingClientRect().left
        }

        for (const el of els) {
          el.style.transition = ''
          el.style.transform = `translateX(${offsetOrigin.current}px)`
        }
      }, [])

    const navigate = useNavigate()

    const navigateTo = useCallback(
      (id: string) => {
        if (navOnQueryParam) {
          params.set(navOnQueryParam, id)
          setParams(params, { replace: true })
        } else {
          navigate(
            `./${id}${params.toString() ? `?${params.toString()}` : ''}`,
            {
              replace: true,
            },
          )
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [navOnQueryParam, params, setParams],
    )

    const handleTouchEnd: React.TouchEventHandler<HTMLDivElement> = useCallback(
      e => {
        const els = [
          prevSlide.current,
          currentSlide.current,
          nextSlide.current,
        ].filter((slide): slide is HTMLDivElement => Boolean(slide))

        const offsetLeft =
          currentSlide.current?.getBoundingClientRect().left || 0
        const width = document.body.clientWidth

        let transform = ''
        const offsetPercent = (offsetLeft / width) * 100
        const transitionTime = 300

        if (offsetPercent > 20 && prevItem) {
          transform = 'translateX(100%)'
          navigateTimeout.current = window.setTimeout(() => {
            navigateTimeout.current = 0
            navigateTo(getMediaKey(prevItem, keyField))
          }, transitionTime - 100)
        } else if (offsetPercent < -20 && nextItem) {
          transform = 'translateX(-100%)'
          navigateTimeout.current = window.setTimeout(() => {
            navigateTimeout.current = 0
            navigateTo(getMediaKey(nextItem, keyField))
          }, transitionTime - 100)
        }

        for (const el of els) {
          el.style.transition = `transform ${transitionTime}ms`
          el.style.transform = transform
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [keyField, navigateTo, nextItem, prevItem],
    )

    const handleTouchMove: React.TouchEventHandler<HTMLDivElement> =
      useCallback(e => {
        const swipePixels =
          e.touches[0].clientX - swipeOrigin.current + offsetOrigin.current
        const els = [
          prevSlide.current,
          currentSlide.current,
          nextSlide.current,
        ].filter((el): el is HTMLDivElement => Boolean(el))

        for (const el of els) {
          el.style.transform = `translateX(${swipePixels}px)`
        }
      }, [])

    const handleKeyDown = useCallback(
      (e: KeyboardEvent) => {
        if (
          editorOpen ||
          e.repeat ||
          e.shiftKey ||
          e.ctrlKey ||
          e.target instanceof HTMLTextAreaElement ||
          (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')
        ) {
          return
        }
        switch (e.key) {
          case 'ArrowLeft':
            {
              prevButton.current?.focus()
              prevButton.current?.click()
            }
            break
          case 'ArrowRight':
            {
              nextButton.current?.focus()
              nextButton.current?.click()
            }
            break

          default:
            break
        }
      },
      [editorOpen],
    )

    useDocumentEventListener('keydown', handleKeyDown)

    // Keep scroll position which is reset by video fullscreen
    useEffect(() => {
      const el = document.scrollingElement

      if (!currentMediaId || !el) {
        return
      }

      const scrollTop = el.scrollTop
      return () => {
        el.scrollTop = scrollTop
      }
    }, [currentMediaId])

    const handleClose = useCallback(
      (open: boolean) => {
        if (open) {
          return
        }

        if (editorOpen) {
          // Esc / outside-click while editor is open routes through the
          // editor's discard-confirm flow rather than dropping unsaved
          // edits. The editor pops the discard modal if there are any
          // edits; otherwise it calls back to `onClose` via the overlay
          // (which calls `setEditorOpen(false)`), giving us the same
          // close path the in-editor X button takes.
          editorRef.current?.requestCancel()
        } else if (showInfoWindow) {
          toggleInfoWindow(false)
        } else if (showPhotoComments) {
          togglePhotoComments(false)
        } else if (onClose) {
          onClose(false)
        }
      },
      [
        editorOpen,
        showInfoWindow,
        showPhotoComments,
        onClose,
        toggleInfoWindow,
        togglePhotoComments,
      ],
    )

    const handleRemoved = () => {
      const targetKey = getMediaCarouselRemovalTargetKey({ nextKey, prevKey })

      if (targetKey) {
        navigateTo(targetKey)
      } else {
        navigate('./', { replace: true })
      }
    }

    return (
      <RadixModal
        variant="dialogs.overlayFileViewer"
        onClose={handleClose}
        {...props}
      >
        <Dialog.Content
          // ref={dialogRef}
          onPointerDownOutside={e => {
            e.preventDefault()
          }}
          onOpenAutoFocus={e => {
            e.preventDefault()
            closeButton.current?.focus()
          }}
        >
          <Dialog.Title className="sr-only">
            <Trans>Media viewer</Trans>
          </Dialog.Title>
          <Dialog.Description className="sr-only">
            <Trans>Use left and right arrow keys to browse photos.</Trans>
          </Dialog.Description>
          <div
            data-testid="Carousel"
            id="carousel"
            className={styles.container}
          >
            <div
              onTouchStart={editorOpen ? undefined : handleTouchStart}
              onTouchMove={editorOpen ? undefined : handleTouchMove}
              onTouchEnd={editorOpen ? undefined : handleTouchEnd}
              className={styles.slideContainer}
            >
              <div className={styles.slides}>
                {prevItem && (
                  <div ref={prevSlide} className={styles.slidePrev}>
                    <MediaView item={prevItem} slideShowMode={slideShowMode} />
                  </div>
                )}
                {prevIndex >= 0 && (
                  <button
                    id="carousel-prev-button"
                    ref={prevButton}
                    disabled={!prevItem}
                    onClick={() =>
                      prevItem && navigateTo(getMediaKey(prevItem, keyField))
                    }
                    className={styles.prevButton}
                    style={{
                      opacity: slideShowMode || playing ? 0 : 1,
                    }}
                  >
                    <PlainBrandIcon icon={SvgCaretLeft} />
                  </button>
                )}
                {currentItem && (
                  <div
                    ref={currentSlide}
                    id="carousel-current-slide"
                    className={styles.slide}
                  >
                    <MediaView
                      item={currentItem}
                      active
                      slideShowMode={slideShowMode}
                      setPlaying={setPlaying}
                    />
                  </div>
                )}
                {!currentItem && (
                  <LoadingOverlay
                    id="MediaCarousel"
                    position="fixed"
                    open
                    darkMode
                  />
                )}
                {nextItem && (
                  <div ref={nextSlide} className={styles.slideNext}>
                    <MediaView item={nextItem} slideShowMode={slideShowMode} />
                  </div>
                )}
                {nextIndex >= 0 && (
                  <button
                    id="carousel-next-button"
                    disabled={!nextItem}
                    onClick={() =>
                      nextItem && navigateTo(getMediaKey(nextItem, keyField))
                    }
                    ref={nextButton}
                    className={styles.nextButton}
                    style={{
                      opacity: slideShowMode || playing ? 0 : 1,
                    }}
                  >
                    <PlainBrandIcon icon={SvgCaretRight} />
                  </button>
                )}
              </div>
              {currentItem && !playing && (
                <div
                  // eslint-disable-next-line tailwindcss/no-custom-classname
                  className={clsx(styles.footer, 'text-white', {
                    'sidepanel-visible':
                      (showInfoWindow || showPhotoComments) && currentItem,
                  })}
                  id="carousel-actions"
                  data-testid="CarouselActions"
                >
                  <PhotoAction.Provider
                    collectionRef={collectionRef}
                    photos={currentItem ? [currentItem] : []}
                    actionScope={actionScope}
                    onTrackAction={
                      actionScope === 'people'
                        ? trackPersonPhotosAction
                        : undefined
                    }
                    actionDispatcherComponent={ActionButton}
                  >
                    {currentItem && currentItem.owner === username && (
                      <Button
                        className="btn btn-ghost text-current"
                        icon={SvgPhotoSearch}
                        onClick={() =>
                          navigate(`/photo?id=${currentItem.idDeprecated}`)
                        }
                      >
                        {md && <Trans>Find similar</Trans>}
                      </Button>
                    )}
                    {canEditCurrentPhoto && (
                      <Button
                        className="btn btn-ghost text-current"
                        icon={SvgSliders}
                        onFocus={() => void loadPhotoEditorOverlay()}
                        onMouseEnter={() => void loadPhotoEditorOverlay()}
                        onClick={() => {
                          trackEvent('PHOTO_EDITOR_START')
                          setEditorOpen(true)
                        }}
                        data-testid="ACTION_EDIT_PHOTO"
                      >
                        {md && <Trans>Edit</Trans>}
                      </Button>
                    )}

                    {commentItemId && (
                      <Button
                        className="btn btn-ghost text-current"
                        icon={SvgChatCircle}
                        onClick={() => togglePhotoComments(true)}
                      >
                        {md && <Trans>Comments</Trans>}
                      </Button>
                    )}
                    <PhotoAction.SharePhotos />
                    <PhotoAction.AddToAlbum />
                    <Button
                      className="btn btn-ghost text-current"
                      onClick={toggleInfoWindow}
                      icon={SvgInfo}
                    >
                      {md && <Trans>Show info</Trans>}
                    </Button>
                    {actionScope === 'default' && (
                      <>
                        <PhotoAction.HidePhotos onSuccess={handleRemoved} />
                        <PhotoAction.UnhidePhotos onSuccess={handleRemoved} />
                        {albumId ? (
                          <PhotoAction.RemoveFromAlbum
                            onSuccess={handleRemoved}
                          />
                        ) : (
                          <PhotoAction.DeletePhotos onSuccess={handleRemoved} />
                        )}
                      </>
                    )}
                    <PhotoAction.DownloadPhotos />
                  </PhotoAction.Provider>
                </div>
              )}
              {currentItem && (
                <Dialog.Close
                  ref={closeButton}
                  id="carousel-close-button"
                  data-testid="CarouselClose"
                  // eslint-disable-next-line tailwindcss/no-custom-classname
                  className={clsx({
                    'carousel-close': true,
                    [styles.closeButton]: true,
                  })}
                >
                  <PlainBrandIcon icon={SvgX} />
                  <span>
                    <Trans>Close</Trans>
                  </span>
                </Dialog.Close>
              )}
              {editorOpen && currentItem && (
                <Suspense
                  fallback={
                    <LoadingOverlay
                      id="PhotoEditorOverlay"
                      open
                      position="absolute"
                      darkMode
                    />
                  }
                >
                  <LazyPhotoEditorOverlay
                    ref={editorRef}
                    item={currentItem}
                    onClose={reason => {
                      if (reason === 'cancel') {
                        trackEvent('PHOTO_EDITOR_CANCELLED')
                      }
                      setEditorOpen(false)
                    }}
                    onSaved={(newId, editState) => {
                      trackEvent('PHOTO_EDITOR_COMPLETE', {
                        filter: hasFilter(editState),
                        rotate: hasRotate(editState),
                        flip: editState.isFlipped,
                        crop: hasCrop(editState),
                        adjustment: hasAdjustments(editState),
                        overlay: hasOverlay(editState),
                      })
                      // The new photo has been imported — navigate the carousel
                      // to it, then dismiss the editor. addFromUpload has
                      // already added it to the timeline so it'll resolve via
                      // the normal usePhoto path.
                      navigateTo(newId)
                      setEditorOpen(false)
                    }}
                  />
                </Suspense>
              )}
            </div>

            <LayoutGroup>
              <AnimatePresence>
                {showInfoWindow && currentItem && (
                  <motion.div
                    className={styles.sidePanel}
                    initial={{ width: 0, opacity: 0 }}
                    animate={{
                      width: branding.isMobileTimeline
                        ? '100%'
                        : 'var(--carousel-sidepanel-width)',
                      opacity: 1,
                    }}
                    transition={{ duration: 0.2 }}
                    exit={{ width: 0, opacity: 0 }}
                  >
                    <PhotoInfo
                      media={currentItem}
                      onClose={toggleInfoWindow}
                      showOwnerName={showOwnerName}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
              <AnimatePresence>
                {showPhotoComments && currentItem && (
                  <motion.div
                    className={styles.sidePanel}
                    initial={{ width: 0, opacity: 0 }}
                    animate={{
                      width: branding.isMobileTimeline
                        ? '100%'
                        : 'var(--carousel-sidepanel-width)',
                      opacity: 1,
                    }}
                    transition={{ duration: 0.2 }}
                    exit={{ width: 0, opacity: 0 }}
                  >
                    <PhotoComments
                      contextKey={commentItemId}
                      albumContextKey={commentsGroupId}
                      onClose={() => togglePhotoComments(false)}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </LayoutGroup>
          </div>
        </Dialog.Content>
      </RadixModal>
    )
  },
)
